import * as React from 'react';
import './WYSIWYGEditor.scss';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Link as LinkIcon,
  OrderedList,
  UnonorderedList
} from '../Icon/icon';
import { Dropbox } from '../Dropbox/dropbox';
import { Buttons, Primary, Secondary } from 'rcd-cpp-ui/ui/button';

interface TipTapProps {
  content: string;
  disabled: boolean;
  readonly?: boolean;
  onChange?: any;
  isFullscreen?: boolean;
}

enum SelectionType {
  NULL = 'NULL',
  LINK = 'LINK'
}

const MenuButton = ({ children, onClick, disabled, className }) => {
  let updatedClassName = 'button';
  if (className) {
    updatedClassName = updatedClassName + ' ' + className;
  }
  if (disabled) {
    updatedClassName = updatedClassName + ' disabled';
  }
  return (
    <div onClick={onClick} className={updatedClassName}>
      {children}
    </div>
  );
};
const MenuBar = ({ editor, addLink }) => {
  if (!editor) {
    return null;
  }
  return (
    <div className="button-bar">
      <MenuButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        disabled={!editor.can().chain().focus().toggleBold().run()}
        className={editor.isActive('bold') ? 'is-active' : ''}
      >
        <Bold />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        disabled={!editor.can().chain().focus().toggleItalic().run()}
        className={editor.isActive('italic') ? 'is-active' : ''}
      >
        <Italic />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        disabled={!editor.can().chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
      >
        <UnderlineIcon />
      </MenuButton>
      <MenuButton
        onClick={addLink}
        className={editor.isActive('link') ? 'is-active' : ''}
        disabled={false}
      >
        <LinkIcon />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        disabled={false}
      >
        <UnonorderedList />
      </MenuButton>
      <MenuButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        disabled={false}
      >
        <OrderedList />
      </MenuButton>
    </div>
  );
};

const baseExtensions = [
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false
    }
  }),
  Underline.configure({})
];

const readOnlyExtensions = [
  ...baseExtensions,
  Link.configure({
    openOnClick: true,
    HTMLAttributes: {
      rel: null,
      target: '_blank'
    }
  })
];

const editModeExtensions = [
  ...baseExtensions,
  Link.configure({
    openOnClick: false,
    HTMLAttributes: {
      rel: null,
      target: '_blank'
    }
  })
];

export const RWWYSIWYGEditor = ({
  content,
  disabled,
  onChange
}: TipTapProps) => {
  const [selectionType, setSelectionType] = React.useState<SelectionType>(null);
  const [selectionURL, setSelectionURL] = React.useState<string>('');
  const editor = useEditor({
    extensions: editModeExtensions,
    injectCSS: true,
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const text = html == '<p></p>' ? '' : html;
      onChange(text);
    }
  });

  const addLink = React.useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    setSelectionURL(previousUrl);
    setSelectionType(SelectionType.LINK);
  }, [editor]);

  const updateLink = () => {
    let url = selectionURL || '';

    // cancelled
    if (url === null) {
      setSelectionType(null);
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setSelectionType(null);
      return;
    }

    // update link
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'http://' + url;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setSelectionType(null);
  };

  return (
    <div className="editor-wrapper wysiwyg-edit-mode">
      <MenuBar editor={editor} addLink={addLink} />

      {selectionType && selectionType == SelectionType.LINK && (
        <Dropbox
          forceShow={selectionType && selectionType == SelectionType.LINK}
          renderBox={setExpanded => (
            <div className="link-container">
              <input
                placeholder="Link URL"
                value={selectionURL}
                onChange={e => setSelectionURL(e.target.value)}
                className="link-input"
                draggable="true"
                onDragStart={e => e.preventDefault()}
              />

              <Buttons>
                <Primary
                  label={'Apply'}
                  onClick={() => {
                    updateLink();
                  }}
                />
                <Secondary
                  label={'Cancel'}
                  onClick={() => {
                    setSelectionType(null);
                  }}
                />
              </Buttons>
            </div>
          )}
        >
          <></>
        </Dropbox>
      )}

      <EditorContent editor={editor} editable={!disabled} />
    </div>
  );
};
